import { z } from "zod";

/**
 * Shared DTO cho Admin Financial Dispute cases (US-AE11.F1-0038 · @UC9100 dispute-case-management,
 * @UC9101 case-ledger-linkage). MỘT nguồn sự thật cho api (S01) + admin web (S03).
 *
 * Case = THEO DÕI read-only 1 khiếu nại tiền: vòng đời open→investigating→resolved + kết luận có audit.
 * DisputeLink = CON TRỎ tham chiếu tới entity nguồn sự thật (entityType+entityId) — KHÔNG copy dữ liệu
 * tiền; số tiền ĐỌC read-only lúc render detail (redaction theo vai).
 *
 * BẤT BIẾN (invariants):
 * - G2: case KHÔNG BAO GIỜ ghi sổ cái (ledger). DTO KHÔNG có field ghi tiền — chỉ theo dõi/kết luận.
 * - C2 no-PII thô: context linked entity chỉ id + status + amount(redact theo vai — null cho vai đọc
 *   thấp), KHÔNG tên/email/phone. Audit no-PII.
 * - Money = minor-unit string (BigInt an toàn qua JSON) — no-float; null khi redact.
 * - `.strict()` cho body ghi (create/link/transition) — chống over-post/field lạ.
 */

// ============================================================
// Enum-lite (khớp badge STATUS admin + mock resources.ts disputes)
// ============================================================

/** Trạng thái vòng đời case (story:1594). open = mới; investigating = đang xử; resolved = đã kết luận. */
export const disputeStatusSchema = z.enum(["open", "investigating", "resolved"]);
export type DisputeStatus = z.infer<typeof disputeStatusSchema>;

/** Mức ưu tiên case (khớp mock resources.ts priority: high/medium/low). */
export const disputePrioritySchema = z.enum(["high", "medium", "low"]);
export type DisputePriority = z.infer<typeof disputePrioritySchema>;

/**
 * Loại entity nguồn sự thật cho link ngữ cảnh (story:1599) — 5 model đều tồn tại trong schema:
 * LedgerTransaction/Collection/MemberDue/Vote/PaymentConfirmation. KHÔNG bịa loại ngoài story.
 */
export const disputeEntityTypeSchema = z.enum([
  "ledger_transaction",
  "collection",
  "member_dues",
  "vote",
  "payment_confirmation",
]);
export type DisputeEntityType = z.infer<typeof disputeEntityTypeSchema>;

// ============================================================
// Linked context (READ — read-only từ nguồn sự thật, redaction vai)
// ============================================================

/**
 * Snapshot read-only 1 entity đã link (đọc lúc render — KHÔNG lưu). `amountMinor` = minor-unit string
 * hoặc null (redact cho vai đọc thấp — SUPPORT/READ_ONLY). `currency` null khi redact. `status` = trạng
 * thái entity (đọc từ nguồn). KHÔNG PII thô (tên/email) — chỉ id + status + amount(theo vai).
 */
export const disputeLinkContextSchema = z
  .object({
    /** Entity còn tồn tại ở nguồn không (link con trỏ có thể trỏ entity đã xoá). */
    exists: z.boolean(),
    /** Trạng thái entity đọc từ nguồn (null nếu entity không tồn tại/không có status). */
    status: z.string().nullable(),
    /** Số tiền minor-unit (string) — null khi redact theo vai HOẶC entity không có tiền. */
    amountMinor: z.string().nullable(),
    /** Mã tiền tệ (VND…) — null khi redact/không áp. */
    currency: z.string().nullable(),
  })
  .strict();

export type DisputeLinkContext = z.infer<typeof disputeLinkContextSchema>;

/**
 * 1 link của case: con trỏ (entityType+entityId) + context read-only (redact theo vai). `note` ghi chú
 * ngữ cảnh (KHÔNG PII/tiền thô).
 */
export const disputeLinkSchema = z
  .object({
    id: z.string(),
    entityType: disputeEntityTypeSchema,
    entityId: z.string(),
    note: z.string().nullable(),
    createdAt: z.string(),
    /** Snapshot read-only entity (đọc lúc GET detail; redaction vai). */
    context: disputeLinkContextSchema,
  })
  .strict();

export type DisputeLink = z.infer<typeof disputeLinkSchema>;

// ============================================================
// Dispute item (list) + detail
// ============================================================

/** 1 dòng case trên board list. reporterUserId/groupId = id (KHÔNG PII thô — resolve tên ở UI ref). */
export const disputeItemSchema = z
  .object({
    id: z.string(),
    code: z.string(),
    subject: z.string(),
    reporterUserId: z.string().nullable(),
    /** Display-name người báo (T01 · @UC9135): username(social)→email→phone. null nếu không resolve. */
    reporterName: z.string().nullable(),
    groupId: z.string().nullable(),
    /** Tên nhóm resolve server-side (T01). null nếu không resolve. */
    groupName: z.string().nullable(),
    priority: disputePrioritySchema,
    status: disputeStatusSchema,
    createdAt: z.string(),
  })
  .strict();

export type DisputeItem = z.infer<typeof disputeItemSchema>;

/** Chi tiết case = item + conclusion + resolvedAt + danh sách link (context read-only redact). */
export const disputeDetailSchema = disputeItemSchema
  .extend({
    conclusion: z.string().nullable(),
    resolvedAt: z.string().nullable(),
    createdById: z.string(),
    updatedAt: z.string(),
    links: z.array(disputeLinkSchema),
  })
  .strict();

export type DisputeDetail = z.infer<typeof disputeDetailSchema>;

/** Envelope list: items + cursor pageInfo `{ data, pageInfo }`. */
export const disputeListResponseSchema = z
  .object({
    data: z.array(disputeItemSchema),
    pageInfo: z
      .object({
        hasNext: z.boolean(),
        nextCursor: z.string().nullable(),
      })
      .strict(),
  })
  .strict();

export type DisputeListResponse = z.infer<typeof disputeListResponseSchema>;

// ============================================================
// Query (GET /admin/disputes)
// ============================================================

export const DISPUTE_LIST_MAX_LIMIT = 100;

/** Query list — filter status/priority, cursor + limit. `.strict()` chặn param lạ. */
export const disputeListQuerySchema = z
  .object({
    status: disputeStatusSchema.optional(),
    priority: disputePrioritySchema.optional(),
    cursor: z.string().max(512).optional(),
    limit: z.coerce.number().int().min(1).max(DISPUTE_LIST_MAX_LIMIT).optional(),
  })
  .strict();

export type DisputeListQuery = z.infer<typeof disputeListQuerySchema>;

// ============================================================
// Create (POST /admin/disputes)
// ============================================================

export const DISPUTE_SUBJECT_MIN_LENGTH = 4;
export const DISPUTE_SUBJECT_MAX_LENGTH = 300;

/**
 * Body mở case. subject bắt buộc (min len). reporterUserId/groupId ≥1 (refine). priority default
 * 'medium'. status LUÔN 'open' khi tạo (server set — KHÔNG nhận từ client). `.strict()`.
 */
export const createDisputeSchema = z
  .object({
    subject: z.string().min(DISPUTE_SUBJECT_MIN_LENGTH).max(DISPUTE_SUBJECT_MAX_LENGTH),
    reporterUserId: z.string().uuid().optional(),
    groupId: z.string().uuid().optional(),
    priority: disputePrioritySchema.optional(),
  })
  .strict()
  .refine((v) => Boolean(v.reporterUserId) || Boolean(v.groupId), {
    message: "Cần ít nhất người báo (reporterUserId) hoặc nhóm (groupId).",
    path: ["reporterUserId"],
  });

export type CreateDisputeBody = z.infer<typeof createDisputeSchema>;

// ============================================================
// Link entity (POST /admin/disputes/:id/link)
// ============================================================

export const DISPUTE_LINK_NOTE_MAX_LENGTH = 300;

/** Body đính kèm entity ngữ cảnh. entityType∈5 loại; entityId uuid; note optional. `.strict()`. */
export const linkEntitySchema = z
  .object({
    entityType: disputeEntityTypeSchema,
    entityId: z.string().uuid(),
    note: z.string().max(DISPUTE_LINK_NOTE_MAX_LENGTH).optional(),
  })
  .strict();

export type LinkEntityBody = z.infer<typeof linkEntitySchema>;

// ============================================================
// Transition status (PATCH /admin/disputes/:id)
// ============================================================

export const DISPUTE_CONCLUSION_MAX_LENGTH = 2000;

/**
 * Body chuyển trạng thái case. status ∈ {investigating,resolved} (open KHÔNG set qua transition — chỉ
 * tiến tới). conclusion BẮT BUỘC khi resolved (refine — kết luận có audit). `.strict()`.
 * KHÔNG có field nào ghi/sửa tiền (G2 — case chỉ theo dõi/kết luận).
 */
export const transitionDisputeSchema = z
  .object({
    status: z.enum(["investigating", "resolved"]),
    conclusion: z.string().max(DISPUTE_CONCLUSION_MAX_LENGTH).optional(),
  })
  .strict()
  .refine((v) => v.status !== "resolved" || Boolean(v.conclusion && v.conclusion.trim().length > 0), {
    message: "Kết luận (conclusion) bắt buộc khi resolve case.",
    path: ["conclusion"],
  });

export type TransitionDisputeBody = z.infer<typeof transitionDisputeSchema>;
